export const DEFAULT_COLLECTION_SORT = 'collection-default';

export const COLLECTION_SORT_OPTIONS: Record<string, { sortKey: string; reverse: boolean }> = {
	[DEFAULT_COLLECTION_SORT]: { sortKey: 'COLLECTION_DEFAULT', reverse: false },
	'best-selling': { sortKey: 'BEST_SELLING', reverse: false },
	'price-asc': { sortKey: 'PRICE', reverse: false },
	'price-desc': { sortKey: 'PRICE', reverse: true },
	newest: { sortKey: 'CREATED', reverse: true },
	'title-asc': { sortKey: 'TITLE', reverse: false },
	'title-desc': { sortKey: 'TITLE', reverse: true },
};

export function getCollectionSortOption(sort: string) {
	return COLLECTION_SORT_OPTIONS[sort] || COLLECTION_SORT_OPTIONS[DEFAULT_COLLECTION_SORT];
}

/**
 * Build Shopify ProductFilter array from URL search params.
 *
 * Supported filter params:
 * - filter.product_type  → { productType: "value" }
 * - filter.availability  → { available: true/false }
 * - filter.price.min / filter.price.max → { price: { min, max } }
 * - filter.trades        → ignored here (handled by collection handle routing)
 */
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: filter translation keeps Shopify query rules in one utility
export function buildShopifyFilters(
	searchParams: URLSearchParams | Record<string, string | string[] | undefined>,
): Record<string, unknown>[] {
	const filters: Record<string, unknown>[] = [];

	const get = (key: string): string | null => {
		if (searchParams instanceof URLSearchParams) {
			return searchParams.get(key);
		}
		const val = searchParams[key];
		return typeof val === 'string' ? val : Array.isArray(val) ? (val[0] ?? null) : null;
	};

	// Product type
	const productType = get('filter.product_type');
	if (productType) {
		for (const pt of productType.split(',')) {
			if (pt.trim()) {
				filters.push({ productType: pt.trim() });
			}
		}
	}

	// Availability
	const availability = get('filter.availability');
	if (availability) {
		const values = availability.split(',');
		if (values.includes('in-stock') && !values.includes('out-of-stock')) {
			filters.push({ available: true });
		} else if (values.includes('out-of-stock') && !values.includes('in-stock')) {
			filters.push({ available: false });
		}
	}

	// Price range
	const priceMin = get('filter.price.min');
	const priceMax = get('filter.price.max');
	if (priceMin || priceMax) {
		const priceFilter: Record<string, number> = {};
		if (priceMin) priceFilter.min = Number(priceMin);
		if (priceMax) priceFilter.max = Number(priceMax);
		filters.push({ price: priceFilter });
	}

	return filters;
}

/**
 * Count active filters in a `filter.*` param map -- mirrors the semantics used
 * by the mobile FilterMobileDrawer's `activeFilterCount` so server-rendered
 * toolbar placeholders show the same "Filters (N)" badge as the hydrated UI.
 *
 * Each comma-separated value on a non-price `filter.X` key counts as one.
 * A non-default price range (only serialized to the URL when user-set) counts
 * as one regardless of whether min or max or both are present.
 */
export function countActiveFilters(filterParams: Record<string, string>): number {
	let count = 0;
	let hasPrice = false;
	for (const [key, value] of Object.entries(filterParams)) {
		if (key === 'filter.price.min' || key === 'filter.price.max') {
			hasPrice = true;
			continue;
		}
		if (key.startsWith('filter.')) {
			count += value.split(',').filter(Boolean).length;
		}
	}
	return count + (hasPrice ? 1 : 0);
}
